import * as yup from 'yup';
import { integerField, plainDateField, usdField } from './common-schema-defs';

// Schema for `GET /metrics/fundmetrics/investor-cash-flows` (FD-2561).

export const InvestorCashFlowTypes = ['GP', 'LP', 'SLP', 'Total'] as const;
export type InvestorCashFlowType = (typeof InvestorCashFlowTypes)[number];

export const InvestorCashFlowNatures = ['Contribution', 'Distribution', 'Expense'] as const;
export type InvestorCashFlowNature = (typeof InvestorCashFlowNatures)[number];

export const InvestorCashFlowSubTypes = [
  'Capital Call',
  'Return of Capital',
  'Realized Gain',
  'Carry',
  'Management Fee',
  'Partnership Expense',
  'Professional Fee',
] as const;
export type InvestorCashFlowSubType = (typeof InvestorCashFlowSubTypes)[number];

export const InvestorCashFlowDistributionTypes = ['Cash', 'Stock', 'Other In-Kind'] as const;
export type InvestorCashFlowDistributionType = (typeof InvestorCashFlowDistributionTypes)[number];

/** A row as the API sends it. The grid row adds `fundName`, joined on from the envelope. */
export function investorCashFlowApiRowFields() {
  return {
    id: integerField().required().meta({ hide: true }),
    investorType: yup.string().oneOf(InvestorCashFlowTypes).required().label('Investor Type'),
    transactionDate: plainDateField().required().label('Date'),
    amount: usdField().required().label('Amount'),
    nature: yup.string().oneOf(InvestorCashFlowNatures).required().label('Nature'),
    distributionType: yup
      .string()
      .oneOf(InvestorCashFlowDistributionTypes)
      .nullable()
      .defined()
      .label('Distribution Type'),
    subType: yup.string().oneOf(InvestorCashFlowSubTypes).nullable().defined().label('Sub-type'),
    // Reconciliation columns: hidden by default, available from the column menu and in the export.
    signedAmount: usdField().required().label('Signed Amount').meta({ hide: true }),
    ledgerTransType: yup.string().required().label('Ledger Type').meta({ hide: true }),
    ledgerAccount: yup.string().nullable().defined().label('Ledger Account').meta({ hide: true }),
    ledgerComment: yup.string().nullable().defined().label('Ledger Comment').meta({ hide: true }),
    transactionEntryId: yup.string().nullable().defined().label('GL Transaction ID').meta({ hide: true }),
  };
}

export function investorCashFlowApiRowSchema() {
  return yup.object(investorCashFlowApiRowFields()).required();
}

export function investorCashFlowRowFields() {
  return {
    fundName: yup.string().required().label('Fund Name'),
    ...investorCashFlowApiRowFields(),
  };
}

export function investorCashFlowRowSchema() {
  return yup.object(investorCashFlowRowFields()).required();
}

export function investorCashFlowsTotalsFields() {
  return {
    contributions: usdField().required(),
    distributions: usdField().required(),
    net: usdField().required(),
    expenses: usdField().required(),
  };
}

export function investorCashFlowsResponseFields() {
  return {
    fundId: integerField().required(),
    fundName: yup.string().required(),
    investorType: yup.string().oneOf(InvestorCashFlowTypes).required(),
    from: yup.string().nullable().defined(), // 'YYYY-MM-DD'
    to: yup.string().nullable().defined(), // 'YYYY-MM-DD'
    includeFeesAndExpenses: yup.boolean().required(),
    rows: yup.array().of(investorCashFlowApiRowSchema()).required(),
    totals: yup.object(investorCashFlowsTotalsFields()).required(),
  };
}

export function investorCashFlowsResponseSchema() {
  return yup.object(investorCashFlowsResponseFields()).required();
}

export type InvestorCashFlowApiRow = yup.InferType<ReturnType<typeof investorCashFlowApiRowSchema>>;
export type InvestorCashFlowRow = yup.InferType<ReturnType<typeof investorCashFlowRowSchema>>;
export type InvestorCashFlowsResponse = yup.InferType<ReturnType<typeof investorCashFlowsResponseSchema>>;

export function createInvestorCashFlowRow(overrides: Partial<InvestorCashFlowRow> = {}): InvestorCashFlowRow {
  return {
    id: 1,
    fundName: '',
    investorType: 'LP',
    transactionDate: '',
    amount: 0,
    nature: 'Contribution',
    distributionType: null,
    subType: 'Capital Call',
    signedAmount: 0,
    ledgerTransType: '',
    ledgerAccount: null,
    ledgerComment: null,
    transactionEntryId: null,
    ...overrides,
  };
}

export function createInvestorCashFlowsResponse(
  overrides: Partial<InvestorCashFlowsResponse> = {}
): InvestorCashFlowsResponse {
  return {
    fundId: 1,
    fundName: '',
    investorType: 'Total',
    from: null,
    to: null,
    includeFeesAndExpenses: false,
    rows: [],
    totals: { contributions: 0, distributions: 0, net: 0, expenses: 0 },
    ...overrides,
  };
}
